// ============================================================================
// safe-write - centralised wrapper for fire-and-forget writes under the
// v5.12.0 DataService contract.
// ----------------------------------------------------------------------------
// Context: v5.12.0 retired the Dexie-first failover. Writes now throw when
// the backend rejects them, instead of silently diverging to Dexie. A prior
// audit identified 15+ call sites using empty `.catch(() => {})` (or
// equivalent noise-suppression catches). Under the new contract those sites
// silently drop data.
//
// This helper:
//   • awaits a promise and returns { ok, value, error }
//   • logs a structured warning to the console (diagnostics)
//   • emits a toast event via `qm:safe-write-fail` so App.jsx can render a
//     single user-visible error message (attribution key = `what`)
//   • dedupes toast emissions so a loop that fails N times doesn't spam the
//     UI with N identical toasts
//
// Usage:
//   import { safeWrite } from '@/services/safe-write';
//   const { ok } = await safeWrite(
//       DataService.projectLearnings.save({ projectId, learningId, stage }),
//       { what: 'projectLearnings.save', context: { projectId, stage } },
//   );
//   if (!ok) return; // caller decides; most fire-and-forget callers ignore
//
// Design notes:
//   • Return `{ ok, value, error }` instead of a bare null so callers that
//     previously checked `if (result)` keep working and new callers can
//     branch on `ok`.
//   • Dedupe is keyed on (what + error.message). Repeats inside a 4-second
// window are swallowed at the toast layer, not at the console layer.
//     every failure still hits the console for triage.
//   • Everything is best-effort. This helper itself must never throw.
// ============================================================================

/**
 * Structural shape of the error values this module formats. `message` is typed
 * `string` because the code consumes it as a string (regex tests + template
 * interpolation), matching how Error.message and API error objects behave; the
 * `|| String(err)` fallbacks cover the absent case. `status` stays `unknown`
 * (only ever compared with ===). The index signature keeps it permissive so
 * callers can pass plain Errors or richer API error objects.
 */
export interface SafeWriteError {
    message?: string;
    status?: unknown;
    [key: string]: unknown;
}

export interface SafeWriteOptions {
    /** Short identifier, e.g. `'projectLearnings.save'`. Used in the toast message, console log, and dedupe key. */
    what?: string;
    /** Optional extra diagnostic context for the console log. */
    context?: unknown;
    /** If true, suppress the toast (still logs). */
    silent?: boolean;
}

export interface SafeWriteResult<T> {
    ok: boolean;
    value: T | null;
    error: unknown;
}

// Per-key cooldown so a loop doesn't spam toasts. 4 seconds is long enough
// to cover a typical synchronous burst (save-all-learnings) without hiding
// intermittent failures that recur over the course of a session.
const TOAST_COOLDOWN_MS = 4000;
const _lastToastAt = new Map<string, number>(); // key -> timestamp

function _dedupeKey(what: string | undefined, err: SafeWriteError | null | undefined): string {
    const msg = err?.message || (err ? String(err) : 'unknown');
    return `${what || 'write'}::${msg}`;
}

function _shouldEmitToast(key: string): boolean {
    const now = Date.now();
    const prev = _lastToastAt.get(key) || 0;
    if (now - prev < TOAST_COOLDOWN_MS) return false;
    _lastToastAt.set(key, now);
    return true;
}

function _emitToast(what: string | undefined, err: SafeWriteError | null | undefined): void {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    const message = _formatMessage(what, err);
    try {
        window.dispatchEvent(new CustomEvent('qm:safe-write-fail', {
            detail: {
                what: what || 'write',
                message,
                errorMessage: err?.message || String(err || ''),
                status: err?.status || null,
                timestamp: Date.now(),
            },
        }));
    } catch { /* non-fatal - event dispatch should never break the app */ }
}

function _formatMessage(what: string | undefined, err: SafeWriteError | null | undefined): string {
    const status = err?.status;
    // Connectivity-class: the banner is already showing; keep the toast short.
    if (status === 500 || status === 502 || status === 503 || status === 504) {
        return `Save failed: backend unreachable (${what}). Your change was NOT saved.`;
    }
    const msg = err?.message || '';
    if (/Failed to fetch/i.test(msg) || /NetworkError/i.test(msg) || /ECONNREFUSED|ETIMEDOUT|ENOTFOUND/i.test(msg)) {
        return `Save failed: backend unreachable (${what}). Your change was NOT saved.`;
    }
    // Data-shape / validation / permission errors.
    if (status === 400 || status === 422) {
        return `Save rejected by server (${what}): ${msg || 'validation error'}`;
    }
    if (status === 401 || status === 403) {
        return `Save blocked (${what}): ${msg || 'not authorised'}`;
    }
    if (status === 404) {
        return `Save failed (${what}): target not found`;
    }
    return `Save failed (${what}): ${msg || 'unknown error'}`;
}

/**
 * Wrap a write promise so failures surface as a toast + structured log
 * instead of being silently swallowed.
 *
 * @param promise - The write promise (e.g. `DataService.x.save(...)`)
 * @param options.what - Short identifier, e.g. `'projectLearnings.save'`.
 *   Used in the toast message, console log, and dedupe key.
 * @param options.context - Optional extra diagnostic context for the console log.
 * @param options.silent - If true, suppress the toast (still logs). Use
 *   sparingly: only for deeply non-critical writes like telemetry on every pipeline step.
 */
export async function safeWrite<T>(promise: Promise<T>, options: SafeWriteOptions = {}): Promise<SafeWriteResult<T>> {
    const { what, context, silent } = options;
    try {
        const value = await promise;
        return { ok: true, value, error: null };
    } catch (err) {
        // `err` is `unknown` (strict catch binding). The casts to SafeWriteError
        // erase at runtime - behaviour is identical to the .js original, which
        // read `err?.message`/`err?.status` directly off the caught value.
        const writeErr = err as SafeWriteError;
        // Always log for console diagnostics.
        try {
            // eslint-disable-next-line no-console
            console.warn(`[safeWrite] ${what || 'write'} failed:`,
                writeErr?.message || err,
                context || '',
            );
        } catch { /* noop */ }

        if (!silent) {
            const key = _dedupeKey(what, writeErr);
            if (_shouldEmitToast(key)) _emitToast(what, writeErr);
        }

        return { ok: false, value: null, error: err };
    }
}

/**
 * Variant for callers that DO want to fire-and-forget from a synchronous
 * context. Equivalent to `safeWrite(promise, opts)` but swallows the returned
 * promise so lint / TypeScript don't flag the unhandled result.
 *
 * Prefer `safeWrite` inside async code. Use `safeWriteFF` only when you're
 * inside a synchronous map/forEach and can't await.
 */
export function safeWriteFF<T>(promise: Promise<T>, options: SafeWriteOptions = {}): void {
    // Intentionally do not return the promise - caller is fire-and-forget.
    safeWrite(promise, options);
}
